import { useEffect, useRef, useState } from 'react'
import type { FeedAttachment } from '../lib/feed'
import { signedPhotoUrl } from '../lib/photos'
import { useHubLang } from '../lib/i18n'
import { COMPOSE } from '../lib/hubCopy'
import { Dialog } from './Dialog'
import { ChevronIcon, PeopleIcon } from './icons'
import { PhotoSuggestionsLine, PhotoTagsLine } from './PhotoTags'
import { PhotoPins, SuggestTagDialog } from './PhotoTagEditor'
import {
  photoTagsFor,
  suggestionsFor,
  suggestRights,
  type PhotoTag,
  type SuggestRights,
  type TagSuggestion,
} from '../lib/photoTags'
import { myPhotoDetails, removePhotoDetail, DETAIL_KEYS, type DetailKey } from '../lib/photos'
import { PhotoReviewButton } from './PhotoReviewButton'
import { PlaceOnMap } from './PlaceOnMap'
import { PhotoWhatDialog, PhotoWhatLine } from './PhotoWhat'
import { photoFeaturesFor, type PhotoFeatures } from '../lib/photoRoom'

/* 161. Whether the viewer shows the names on the photo. Off at first, as
   names over faces would be; once someone turns it on it stays on for the
   rest of the visit, photo to photo and post to post. */
let showTagsThisVisit = false

/**
 * A post's photos, one at a time and as large as the screen allows (141).
 *
 * David, 9 Oct: "click a photo in the feed and see it larger, maybe it opens a
 * modal? and then to scroll through the photos."
 *
 * ── IT IS A Dialog, NOT A SECOND MODAL ─────────────────────────────────────
 *
 * Dialog already does the focus trap, Escape, focus return to the tile, the
 * scroll lock and the surface stack. A lightbox that re-implemented those would
 * be the fifth copy of the Escape bug. `className="lightbox"` re-skins the
 * panel dark and full-bleed; everything behavioural is Dialog's.
 *
 * ── MOVING BETWEEN PHOTOS ──────────────────────────────────────────────────
 *
 * The post's photos only, not the whole feed: the feed's next photo belongs to
 * a different post with a different author and audience, and a viewer that
 * walks across that line loses the context the caption depends on.
 *
 * Arrow keys, the two buttons, or a sideways swipe. It STOPS at the ends rather
 * than wrapping, so "Photo 5 of 5" and a disabled Next say the same thing.
 * The title is the counter, so a screen reader hears where it is on each move.
 *
 * ── THE NEXT PHOTO IS ALREADY ON ITS WAY ───────────────────────────────────
 *
 * Each move signs and pre-loads its neighbours. Photos are ~1 MB at 2000px;
 * without the look-ahead every swipe is a grey box on a site connection.
 */
export function PhotoLightbox({
  attachments,
  index,
  onIndex,
  onClose,
}: {
  attachments: FeedAttachment[]
  index: number
  onIndex: (i: number) => void
  onClose: () => void
}) {
  const t = COMPOSE[useHubLang()]
  const n = attachments.length
  const a = attachments[index]
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const touch = useRef<{ x: number; y: number } | null>(null)
  /* 143 (FED-49). Who is in each of this post's photos, read once when the
     viewer opens and again after a tag is removed. A failed read shows no
     names rather than an error: the photo is what was asked for. */
  const [tags, setTags] = useState<PhotoTag[]>([])
  const [tagsRev, setTagsRev] = useState(0)
  /* 161. Pins on the photo: the toggle, and the name the line below points at. */
  const [showTags, setShowTags] = useState(showTagsThisVisit)
  const [focusTag, setFocusTag] = useState<string | null>(null)
  useEffect(() => setFocusTag(null), [a.path])
  const pathsKey = attachments.map((x) => x.path).join('|')
  useEffect(() => {
    let live = true
    photoTagsFor(attachments.map((x) => x.path))
      .then((r) => live && setTags(r))
      .catch(() => {})
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey, tagsRev])

  /* 163. Suggested tags you may see, and what you may do on each photo. Read
     with the tags and again after any change. A failed read offers nothing. */
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([])
  const [rights, setRights] = useState<Map<string, SuggestRights>>(new Map())
  const [suggesting, setSuggesting] = useState(false)
  useEffect(() => {
    let live = true
    const paths = attachments.map((x) => x.path)
    Promise.all([suggestionsFor(paths), suggestRights(paths)])
      .then(([s, r]) => {
        if (!live) return
        setSuggestions(s)
        setRights(r)
      })
      .catch(() => {})
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey, tagsRev])

  /* 149 (FED-54). Your own photos only: whether a location or camera and
     date are held, so you can take them back. The server answers only for
     the caller's own paths; anybody else's viewer shows nothing. */
  const [details, setDetails] = useState<Map<string, { location: boolean; camera: boolean }>>(new Map())
  const [removed, setRemoved] = useState<{ path: string; which: DetailKey } | null>(null)
  useEffect(() => {
    let live = true
    myPhotoDetails(attachments.map((x) => x.path))
      .then((m) => live && setDetails(m))
      .catch(() => {})
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey])

  /* 302 (FED-70) / 305 (FED-72). One's tags on every photo you can see in
     the Gallery: read with the post, again after you answer. */
  const [what, setWhat] = useState<Map<string, PhotoFeatures>>(new Map())
  const [whatRev, setWhatRev] = useState(0)
  const [checking, setChecking] = useState(false)
  useEffect(() => {
    let live = true
    photoFeaturesFor(attachments.map((x) => x.path))
      .then((m) => live && setWhat(m))
      .catch(() => {})
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey, whatRev])
  const whatHere = what.get(a.path) ?? null

  const here = tags.filter((x) => x.path === a.path)
  const suggestedHere = suggestions.filter((x) => x.path === a.path)
  const placedHere =
    here.filter((x) => x.x !== null && x.y !== null).length +
    suggestedHere.filter((x) => x.x !== null && x.y !== null).length

  const extraLines =
    suggestedHere.length +
    (suggestedHere.some((x) => x.canDecide) ? 1 : 0) +
    (rights.get(a.path)?.maySuggest ? 1 : 0) +
    (whatHere ? 1 : 0)

  const hasPrev = index > 0
  const hasNext = index < n - 1

  useEffect(() => {
    let live = true
    setUrl(null)
    setFailed(false)
    signedPhotoUrl(a.path)
      .then((u) => {
        if (!live) return
        if (u) setUrl(u)
        else setFailed(true)
      })
      .catch(() => live && setFailed(true))

    for (const j of [index + 1, index - 1]) {
      const near = attachments[j]
      if (!near) continue
      signedPhotoUrl(near.path)
        .then((u) => {
          if (u) new Image().src = u
        })
        .catch(() => {})
    }
    return () => {
      live = false
    }
  }, [a.path, index, attachments])

  // Arrow keys from anywhere while the viewer is open. Dialog's own key
  // handler only hears Escape and Tab, and the focused element is usually the
  // panel itself, which children cannot listen on.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // 163: arrows belong to the suggest dialog's search while it is open.
      if (suggesting || checking) return
      if (e.key === 'ArrowLeft' && hasPrev) {
        e.preventDefault()
        onIndex(index - 1)
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault()
        onIndex(index + 1)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [index, hasPrev, hasNext, onIndex, suggesting, checking])

  function onTouchStart(e: React.TouchEvent) {
    const p = e.touches[0]
    touch.current = p ? { x: p.clientX, y: p.clientY } : null
  }

  function onTouchEnd(e: React.TouchEvent) {
    const start = touch.current
    touch.current = null
    const p = e.changedTouches[0]
    if (!start || !p) return
    const dx = p.clientX - start.x
    const dy = p.clientY - start.y
    // Sideways and deliberate. A vertical drag is someone trying to scroll a
    // long caption, and a 20px wobble is a tap.
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return
    if (dx < 0 && hasNext) onIndex(index + 1)
    if (dx > 0 && hasPrev) onIndex(index - 1)
  }

  return (
    <Dialog
      open
      title={n > 1 ? t.photoOf(index + 1, n) : t.photo}
      onClose={onClose}
      className="lightbox"
    >
      <figure
        className="lightbox__figure"
        /* 163: each line under the photo takes room the image must give up,
           or the last line slides under the frame. */
        style={{ '--lightbox-extra': `${extraLines * 2.2}rem` } as React.CSSProperties}
      >
        <div className="lightbox__stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {failed ? (
            <p className="lightbox__failed">{t.photoFailed}</p>
          ) : url ? (
            /* 161. The frame is exactly the image's box, so a pin at 40% / 30%
               lands on the same face at any screen size. */
            <div className="pinframe">
              <img
                key={a.id}
                className="lightbox__img"
                src={url}
                /* The caption, when there is one, is the figcaption below; an
                   alt repeating it would be read twice. */
                alt={a.alt ? '' : t.photoOf(index + 1, n)}
                width={a.width}
                height={a.height}
                onError={() => setFailed(true)}
              />
              <PhotoPins tags={here} show={showTags} focus={focusTag} suggested={suggestedHere} />
              {placedHere > 0 && (
                <button
                  type="button"
                  className={`pinframe__toggle${showTags ? ' is-on' : ''}`}
                  aria-pressed={showTags}
                  aria-label={showTags ? t.hideTags : t.showTags(placedHere)}
                  title={showTags ? t.hideTags : t.showTags(placedHere)}
                  onClick={() => {
                    showTagsThisVisit = !showTags
                    setShowTags(!showTags)
                  }}
                >
                  <PeopleIcon size={16} />
                  <span aria-hidden="true">{placedHere}</span>
                </button>
              )}
            </div>
          ) : (
            <div
              className="lightbox__holder"
              style={{ aspectRatio: `${a.width} / ${a.height}` }}
              aria-hidden="true"
            />
          )}

          {n > 1 && (
            <>
              <button
                type="button"
                className="lightbox__nav lightbox__nav--prev"
                onClick={() => onIndex(index - 1)}
                disabled={!hasPrev}
                aria-label={t.previousPhoto}
              >
                <ChevronIcon size={28} />
              </button>
              <button
                type="button"
                className="lightbox__nav lightbox__nav--next"
                onClick={() => onIndex(index + 1)}
                disabled={!hasNext}
                aria-label={t.nextPhoto}
              >
                <ChevronIcon size={28} />
              </button>
            </>
          )}
        </div>
        {a.alt && <figcaption className="lightbox__caption">{a.alt}</figcaption>}
        <PhotoTagsLine
          path={a.path}
          tags={here}
          onChanged={() => setTagsRev((r) => r + 1)}
          onFocusPerson={setFocusTag}
        />
        <PhotoSuggestionsLine
          suggestions={suggestedHere}
          rights={rights.get(a.path) ?? null}
          onSuggest={() => setSuggesting(true)}
          onChanged={() => setTagsRev((r) => r + 1)}
          onFocusPerson={setFocusTag}
        />
        {whatHere && <PhotoWhatLine info={whatHere} onOpen={() => setChecking(true)} />}
        {checking && whatHere && (
          <PhotoWhatDialog
            path={a.path}
            info={whatHere}
            onChanged={() => setWhatRev((r) => r + 1)}
            onClose={() => setChecking(false)}
          />
        )}
        {suggesting && (
          <SuggestTagDialog
            path={a.path}
            src={url}
            width={a.width}
            height={a.height}
            tags={here}
            suggestions={suggestedHere}
            onChanged={() => setTagsRev((r) => r + 1)}
            onClose={() => setSuggesting(false)}
          />
        )}
        {/* 152. Anybody who can see the photo may ask for a review; the
            control hides itself on the poster's own photo. */}
        <PhotoReviewButton key={a.path} path={a.path} className="lightbox__review" />
        {/* 150: camera details and location are taken back separately. */}
        {(removed?.path === a.path || DETAIL_KEYS.some((k) => details.get(a.path)?.[k])) && (
          <p className="lightbox__details">
            {removed?.path === a.path && <span role="status">{t.detailRemoved[removed.which]} </span>}
            {DETAIL_KEYS.filter((k) => details.get(a.path)?.[k]).map((k) => (
              <button
                key={k}
                type="button"
                className="btn btn--link"
                onClick={() => {
                  removePhotoDetail(a.path, k)
                    .then(() => {
                      setRemoved({ path: a.path, which: k })
                      setDetails((m) => {
                        const cur = m.get(a.path) ?? { location: false, camera: false }
                        return new Map(m).set(a.path, { ...cur, [k]: false })
                      })
                    })
                    .catch(() => {})
                }}
              >
                {t.removeDetail[k]}
              </button>
            ))}
          </p>
        )}
        {/* 319: the poster adds or moves where their photo was taken.
            324: and sees it on a small map (David, 30 Oct: "a small map view
            on the photo detail modal"). Still the poster's own photo only:
            the feed never shows where a colleague's photo was taken. */}
        <PlaceOnMap key={a.path} path={a.path} onlyMine showMap className="placeonmap lightbox__place" />
      </figure>
    </Dialog>
  )
}
